/**
 * Route Details page — displays products available on the route.
 * Accessed via /routes/:routeId/details
 *
 * This page requires activeProductsRouteId to be set in the map store
 * (done by RoutesPage.handleShowProducts before navigating here).
 * On direct access or page refresh the store state is lost, so we
 * redirect back to the route list.
 */
import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMapStore } from '@shared/hooks/useMapStore';
import ActiveRoutePanel from './components/ActiveRoutePanel';

export default function RouteDetailPage() {
  const { routeId } = useParams<{ routeId: string }>();
  const navigate = useNavigate();
  const activeProductsRouteId = useMapStore((s) => s.activeProductsRouteId);
  const setActiveProductsRouteId = useMapStore((s) => s.setActiveProductsRouteId);
  const setSelectedRouteId = useMapStore((s) => s.setSelectedRouteId);

  // Redirect to route list when accessed without store state (e.g. page refresh)
  useEffect(() => {
    if (!activeProductsRouteId) {
      navigate('/routes', { replace: true });
    }
  }, [activeProductsRouteId, navigate]);

  function handleBack() {
    setActiveProductsRouteId(null);
    setSelectedRouteId(null);
    navigate('/routes');
  }

  function handleEdit() {
    navigate(`/routes/${routeId}/edit`, { state: { returnTo: 'details' } });
  }

  if (!routeId || !activeProductsRouteId) return null;

  return (
    <div data-testid="page-route-details" className="flex flex-col h-full">
      <ActiveRoutePanel routeId={routeId} onBack={handleBack} onEdit={handleEdit} />
    </div>
  );
}
